import {
    fetchMyRegisterContactAddresses,
    saveMyRegisterContactAddresses,
    type MyRegisterContactAddressSavePayload,
} from '@/features/register/MY/api/myRegisterApi';
import {
    MY_REGISTER_POSITION_CD,
    resolveRegisterContactCategoryCd,
    shouldUseRegisterCorporateBranchSave,
} from '@/features/register/MY/constants/myRegisterPositionCd';
import {
    normalizeRegisterAddressListForSave,
    normalizeRegisterBranchAddressListForSave,
    normalizeRegisterAddressRowForSave,
    normalizeRegisterBranchAddressRowForSave,
} from '@/features/register/MY/utils/registerAddressSaveNormalize';
import { stripRegisterAddressFieldsFromContactUpdate } from '@/features/register/MY/utils/saveMyRegisterRoleContactAddresses';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import type { GlobalContactMemberForm } from './globalContactMemberDisplay';
import {
    addressRowToMyForm,
    buildContactUpdatePayload,
    myFormToAddressPayload,
} from './globalContactMemberDisplay';
import type {
    EntityBeneficialOwnerContactOverlay,
    EntityOfficerContactOverlay,
    EntityOfficerPositionGroup,
} from '../types/entity.types';

const POSITION_GROUP_TO_CD: Record<EntityOfficerPositionGroup, string> = {
    director: MY_REGISTER_POSITION_CD.DIRECTOR,
    secretary: MY_REGISTER_POSITION_CD.SECRETARY,
    auditor: MY_REGISTER_POSITION_CD.AUDITOR,
    manager: MY_REGISTER_POSITION_CD.MANAGER,
    taxAgent: MY_REGISTER_POSITION_CD.TAX_AGENT,
};

export type EntityRegisterDetailAddressContext = {
    entityCd: string;
    contactCd: string;
    positionCd: string;
    categoryCd: string;
};

function pickPrimaryAddressRow(rows: unknown): Record<string, unknown> | null {
    if (rows == null) return null;
    if (Array.isArray(rows)) {
        if (!rows.length) return null;
        const primary = rows.find((item) => {
            const row = item as Record<string, unknown>;
            const flag = row.isPrimaryAddress ?? row.is_primary_address;
            return flag === 1 || flag === '1' || flag === true;
        });
        if (primary && typeof primary === 'object') return { ...(primary as object) };
        const first = rows.find((item) => item && typeof item === 'object');
        return first && typeof first === 'object' ? { ...(first as object) } : null;
    }
    if (typeof rows === 'object') return { ...(rows as object) };
    return null;
}

export function resolveEntityRegisterDetailAddressContext(opts: {
    entityCd?: string;
    contactCd?: string;
    registerRolePositionCd?: string;
    entityOfficerOverlay?: EntityOfficerContactOverlay | null;
    entityBeneficialOwnerOverlay?: EntityBeneficialOwnerContactOverlay | null;
    memberEntityRole?: boolean;
}): EntityRegisterDetailAddressContext | null {
    const entityCd = String(
        opts.entityCd
        || opts.entityOfficerOverlay?.entityCd
        || opts.entityBeneficialOwnerOverlay?.entityCd
        || '',
    ).trim();
    const contactCd = String(opts.contactCd || '').trim();
    if (!entityCd || !contactCd) return null;

    let positionCd = String(opts.registerRolePositionCd || '').trim();
    if (!positionCd && opts.memberEntityRole) {
        positionCd = MY_REGISTER_POSITION_CD.MEMBER;
    }
    if (!positionCd && opts.entityBeneficialOwnerOverlay) {
        positionCd = MY_REGISTER_POSITION_CD.BENEFICIAL_OWNER;
    }
    if (!positionCd && opts.entityOfficerOverlay?.positionCode) {
        positionCd = String(opts.entityOfficerOverlay.positionCode).trim();
    }
    if (!positionCd && opts.entityOfficerOverlay?.positionGroup) {
        positionCd = POSITION_GROUP_TO_CD[opts.entityOfficerOverlay.positionGroup] || '';
    }
    if (!positionCd) return null;

    return {
        entityCd,
        contactCd,
        positionCd,
        categoryCd: 'B09002',
    };
}

export async function fetchEntityRegisterContactLikeForDetail(
    ctx: EntityRegisterDetailAddressContext,
    categoryCd?: string,
): Promise<Record<string, unknown>> {
    const cat = String(categoryCd || ctx.categoryCd || 'B09002').trim();
    const { contactLike } = await fetchMyRegisterContactAddresses(
        ctx.entityCd,
        ctx.contactCd,
        ctx.positionCd,
        cat,
    );
    return (contactLike || {}) as Record<string, unknown>;
}

/** Global Contact + Register 역할별 주소 병합 (프로필 필드는 base 유지) */
export function mergeBaseContactWithRegisterAddressLike(
    baseContact: Record<string, unknown>,
    contactLike: Record<string, unknown>,
): Record<string, unknown> {
    return {
        ...baseContact,
        ...contactLike,
        CONTACT_CD: baseContact.CONTACT_CD ?? baseContact.contactCd,
        CONTACT_NAME: baseContact.CONTACT_NAME ?? baseContact.contactName,
        CONTACT_REG_NO: baseContact.CONTACT_REG_NO ?? baseContact.contactRegNo,
        CATEGORY_CD: baseContact.CATEGORY_CD ?? baseContact.categoryCd ?? contactLike.CATEGORY_CD,
        ID_TYPE_CD: baseContact.ID_TYPE_CD ?? baseContact.idTypeCd,
        NATIONALITY_CD: baseContact.NATIONALITY_CD ?? baseContact.nationalityCd,
        DATE_OF_BIRTH: baseContact.DATE_OF_BIRTH ?? baseContact.dateOfBirth,
        CONTACT_EMAIL: baseContact.CONTACT_EMAIL ?? baseContact.contactEmail,
        USE_BRANCH_ADDRESSES: contactLike.USE_BRANCH_ADDRESSES ?? true,
        residential_address: contactLike.residential_address ?? baseContact.residential_address,
        service_address: contactLike.service_address ?? baseContact.service_address,
        business_address: contactLike.business_address ?? baseContact.business_address,
        registeredAddress: contactLike.registeredAddress ?? baseContact.registeredAddress,
    };
}

export function pickRegisterResidentialRow(contactLike: Record<string, unknown> | null | undefined) {
    return pickPrimaryAddressRow(contactLike?.residential_address);
}

export function pickRegisterServiceRow(contactLike: Record<string, unknown> | null | undefined) {
    return pickPrimaryAddressRow(contactLike?.service_address);
}

export type EntityMyAddressFormFields = {
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    country?: string;
    state?: string;
    city?: string;
    postcode?: string;
    addressId?: number;
    branchId?: number;
};

export function registerAddressRowToFields(
    row: Record<string, unknown> | null | undefined,
): EntityMyAddressFormFields | null {
    if (!row) return null;
    const form = addressRowToMyForm(row);
    const hasContent = [
        form.addressLine1,
        form.addressLine2,
        form.addressLine3,
        form.postcode,
        form.city,
        form.state,
        form.country,
    ].some((v) => String(v ?? '').trim() !== '');
    return hasContent ? form : null;
}

function hasAddressFieldsContent(fields: EntityMyAddressFormFields | null | undefined): boolean {
    if (!fields) return false;
    return [
        fields.addressLine1,
        fields.addressLine2,
        fields.addressLine3,
        fields.postcode,
        fields.city,
        fields.state,
        fields.country,
    ].some((v) => String(v ?? '').trim() !== '');
}

function addressFieldsToSaveRow(
    fields: EntityMyAddressFormFields,
    entityCd: string,
    positionName: string,
    categoryCd: string,
    positionCd: string,
): Record<string, unknown> {
    const useBranchCodes = shouldUseRegisterCorporateBranchSave(categoryCd, positionCd);
    const raw = {
        ...myFormToAddressPayload({
            addressLine1: fields.addressLine1 ?? '',
            addressLine2: fields.addressLine2 ?? '',
            addressLine3: fields.addressLine3 ?? '',
            country: fields.country ?? '',
            state: fields.state ?? '',
            city: fields.city ?? '',
            postcode: fields.postcode ?? '',
            addressId: fields.addressId,
            branchId: fields.branchId ?? fields.addressId,
        }),
        entity_cd: entityCd,
        position: positionName,
    };
    return useBranchCodes
        ? normalizeRegisterBranchAddressRowForSave(raw)
        : normalizeRegisterAddressRowForSave(raw);
}

function cloneAddressRowsForRegisterSave(
    rows: unknown[] | null | undefined,
    useBranchCodes: boolean,
): Record<string, unknown>[] {
    return useBranchCodes
        ? normalizeRegisterBranchAddressListForSave(rows)
        : normalizeRegisterAddressListForSave(rows);
}

/** Entity MY — Register 역할별 주소 INSERT/UPDATE (L_CONTACT_BRANCH_MAP + L_CONTACT_ADDRESS) */
export async function saveEntityRegisterRoleAddressesFromForms(opts: {
    entityCd: string;
    contactCd: string;
    positionCd: string;
    positionName: string;
    categoryCd?: string;
    residential?: EntityMyAddressFormFields | null;
    service?: EntityMyAddressFormFields | null;
    business?: unknown[] | null;
}): Promise<void> {
    const entityCd = String(opts.entityCd || '').trim();
    const contactCd = String(opts.contactCd || '').trim();
    const positionCd = String(opts.positionCd || '').trim();
    const positionName = String(opts.positionName || '').trim();
    const categoryCd = resolveRegisterContactCategoryCd(undefined, opts.categoryCd);
    if (!entityCd || !contactCd || !positionCd) {
        throw new Error('entityCd, contactCd, and positionCd are required');
    }

    const useBranchCodes = shouldUseRegisterCorporateBranchSave(categoryCd, positionCd);
    const payload: MyRegisterContactAddressSavePayload = {
        positionCd,
        positionName,
        categoryCd,
        residential: [],
        service: [],
        business: [],
    };

    if (hasAddressFieldsContent(opts.residential)) {
        payload.residential = [
            addressFieldsToSaveRow(opts.residential!, entityCd, positionName, categoryCd, positionCd),
        ];
    }
    if (hasAddressFieldsContent(opts.service)) {
        payload.service = [
            addressFieldsToSaveRow(opts.service!, entityCd, positionName, categoryCd, positionCd),
        ];
    }
    if (Array.isArray(opts.business) && opts.business.length > 0) {
        payload.business = cloneAddressRowsForRegisterSave(opts.business, useBranchCodes);
    }

    const hasAny =
        (payload.residential?.length ?? 0) > 0
        || (payload.service?.length ?? 0) > 0
        || (payload.business?.length ?? 0) > 0;
    if (!hasAny) return;

    await saveMyRegisterContactAddresses(entityCd, contactCd, payload);
}

/** Entity Members 탭 — Register 역할(B30034) 주소 UPDATE + Global Contact 프로필 저장 */
export async function saveEntityMemberRegisterContact(opts: {
    form: GlobalContactMemberForm;
    entityCd: string;
    shareholderName?: string;
}): Promise<void> {
    const entityCd = String(opts.entityCd || '').trim();
    const contactCd = String(opts.form.contactCd || '').trim();
    if (!entityCd || !contactCd) {
        throw new Error('entityCd and contactCd are required');
    }

    const categoryCd = String(opts.form.categoryCd || 'B09002').trim();
    const contactUpdate = buildContactUpdatePayload(opts.form, entityCd, opts.shareholderName);

    await saveEntityRegisterRoleAddressesFromForms({
        entityCd,
        contactCd,
        positionCd: MY_REGISTER_POSITION_CD.MEMBER,
        positionName: 'Shareholder',
        categoryCd,
        residential: opts.form.residential,
        service: opts.form.service,
    });

    const profileOnly = stripRegisterAddressFieldsFromContactUpdate(
        contactUpdate as Record<string, unknown>,
    );
    await contactsApi.updateContact(contactCd, profileOnly as any);
}

/** Entity Beneficial Owner — Register 역할(B30046) 주소 INSERT/UPDATE */
export async function saveEntityBeneficialOwnerRegisterContact(opts: {
    entityCd: string;
    contactCd: string;
    categoryCd?: string;
    residential?: EntityMyAddressFormFields | null;
    business?: unknown[] | null;
}): Promise<void> {
    await saveEntityRegisterRoleAddressesFromForms({
        entityCd: opts.entityCd,
        contactCd: opts.contactCd,
        positionCd: MY_REGISTER_POSITION_CD.BENEFICIAL_OWNER,
        positionName: 'Beneficial Owner',
        categoryCd: opts.categoryCd,
        residential: opts.residential,
        business: opts.business,
    });
}
